import { clsx } from 'clsx';
import { useId } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Container } from './Container';
import type { ContainerSize } from './Container';

const HEADING_CLASS = { 1: 't-h1', 2: 't-h2', 3: 't-h3' } as const;

export interface SectionProps extends Omit<ComponentPropsWithoutRef<'section'>, 'title'> {
  /** Band colour: page basalt or raised charcoal. Alternate them down a page. */
  tone?: 'bg' | 'surface';
  /** Hairline above the band (default on). */
  divider?: boolean;
  align?: 'start' | 'center';
  /** Small uppercase brass label above the heading. */
  eyebrow?: ReactNode;
  /** Heading text; the section is labelled by it (`aria-labelledby`). */
  title?: ReactNode;
  /** Semantic level of the heading — use 1 once per page. Default 2. */
  headingLevel?: 1 | 2 | 3;
  containerSize?: ContainerSize;
}

/**
 * A content band: fluid vertical rhythm (`--space-section`), optional hairline, eyebrow + heading
 * slot, and a `Container` for the children.
 */
export function Section({
  tone = 'bg',
  divider = true,
  align = 'start',
  eyebrow,
  title,
  headingLevel = 2,
  containerSize,
  className,
  children,
  ...props
}: SectionProps) {
  const headingId = useId();
  const Heading = `h${String(headingLevel)}` as 'h1' | 'h2' | 'h3';

  return (
    <section
      className={clsx('section', className)}
      data-tone={tone}
      data-divider={divider}
      data-align={align}
      aria-labelledby={title ? headingId : undefined}
      {...props}
    >
      <Container {...(containerSize ? { size: containerSize } : {})}>
        {eyebrow || title ? (
          <div className="section__head">
            {eyebrow ? <p className="t-eyebrow">{eyebrow}</p> : null}
            {title ? (
              <Heading id={headingId} className={HEADING_CLASS[headingLevel]}>
                {title}
              </Heading>
            ) : null}
          </div>
        ) : null}
        {children}
      </Container>
    </section>
  );
}
